import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { 
  Upload, Camera, FileText, DollarSign,
  Clock, CheckCircle, XCircle, Banknote,
  Loader2, AlertCircle, Plus, Wine, GlassWater
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = 'copper', prefix = '' }) {
  const colors = {
    copper: 'bg-copper/10 text-copper',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy">{prefix}{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function RepDashboard({ PaymentMessage }) {
  const { user, token, logout } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'on_premise',
    placement_type: 'new_btg',
    photo_url: '',
    order_doc_url: '',
    notes: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ state: 'idle', msg: '' });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        fetch(`${API}/proofs/my-submissions`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/proofs/my-stats`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (subsRes.ok) {
        const subsData = await subsRes.json();
        setSubmissions(subsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProof = async (e) => {
    e.preventDefault();
    
    if (!formData.photo_url) {
      setSubmitStatus({ state: 'error', msg: 'Photo URL is required' });
      return;
    }

    setSubmitting(true);
    setSubmitStatus({ state: 'idle', msg: '' });

    try {
      const res = await fetch(`${API}/proofs/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowSubmitModal(false);
        setFormData({
          account_name: '',
          account_type: 'on_premise',
          placement_type: 'new_btg',
          photo_url: '',
          order_doc_url: '',
          notes: ''
        });
        fetchData();
      } else {
        const data = await res.json();
        setSubmitStatus({ state: 'error', msg: data.detail || 'Submission failed' });
      }
    } catch (err) {
      setSubmitStatus({ state: 'error', msg: 'Network error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-copper" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        <div className="container-custom py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                Rep Dashboard
              </h1>
              <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="text-sm text-slate-600 hover:text-copper"
            >
              Sign Out
            </button>
          </div>

          <PaymentMessage />

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FileText} label="Total Submissions" value={stats.total_submissions || 0} color="blue" />
            <StatCard icon={Clock} label="Pending Review" value={stats.pending || 0} color="yellow" />
            <StatCard icon={CheckCircle} label="Approved" value={stats.approved || 0} color="green" />
            <StatCard icon={DollarSign} label="Total Earned" value={stats.total_earned || 0} color="copper" prefix="$" />
          </div>

          {/* Earnings Summary */}
          <div className="bg-gradient-to-r from-navy to-navy/90 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1">Your Earnings</h3>
                <div className="flex items-baseline gap-4">
                  <div>
                    <span className="text-3xl font-bold">${stats.total_earned || 0}</span>
                    <span className="text-white/70 ml-2">paid</span>
                  </div>
                  {stats.pending_payout > 0 && (
                    <div className="text-white/80">
                      <span className="text-xl font-semibold">${stats.pending_payout}</span>
                      <span className="text-white/60 ml-1">pending</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="btn-copper"
                data-testid="submit-proof-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit New Proof
              </button>
            </div>
          </div>

          {/* Incentive Rates */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Wine className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">$200</p>
                <p className="text-sm text-slate-600">New BTG / Cocktail Program</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <GlassWater className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">$100</p>
                <p className="text-sm text-slate-600">Feature BTG / Cocktail</p>
              </div>
            </div>
          </div>

          {/* Submissions List */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-navy text-lg mb-6">Your Submissions</h2>

            {submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No submissions yet</p>
                <p className="text-sm mt-1">Submit your first placement proof to start earning</p>
                <button
                  onClick={() => setShowSubmitModal(true)}
                  className="mt-4 btn-copper"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Proof
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="border border-slate-200 rounded-xl p-4 hover:border-copper/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-navy">{sub.account_name}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                          <span className="capitalize">{sub.account_type?.replace('_', '-')}</span>
                          <span>•</span>
                          <span>{sub.placement_type === 'new_btg' ? 'New Program' : 'Feature'}</span>
                        </div>
                        {sub.notes && (
                          <p className="text-sm text-slate-500 mt-2">{sub.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          sub.status === 'paid' ? 'bg-green-100 text-green-700' :
                          sub.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          sub.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {sub.status}
                        </span>
                        <p className="text-lg font-bold text-navy mt-2">${sub.payout_amount}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 my-8">
            <h3 className="font-semibold text-navy text-lg mb-4">
              Submit Placement Proof
            </h3>

            <form onSubmit={handleSubmitProof} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  required
                  className="input-field"
                  placeholder="e.g., The Ritz-Carlton Waikiki"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Account Type
                  </label>
                  <select
                    value={formData.account_type}
                    onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                    className="input-field"
                  >
                    <option value="on_premise">On-Premise</option>
                    <option value="off_premise">Off-Premise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Placement Type
                  </label>
                  <select
                    value={formData.placement_type}
                    onChange={(e) => setFormData({ ...formData, placement_type: e.target.value })}
                    className="input-field"
                  >
                    <option value="new_btg">New BTG/Cocktail ($200)</option>
                    <option value="feature_btg">Feature BTG/Cocktail ($100)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Menu Photo URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  required
                  className="input-field"
                  placeholder="https://..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Upload your photo to a service like Imgur or Google Drive and paste the link
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Order/Invoice URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.order_doc_url}
                  onChange={(e) => setFormData({ ...formData, order_doc_url: e.target.value })}
                  className="input-field"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="input-field resize-none"
                  placeholder="Any additional context..."
                />
              </div>

              {submitStatus.state === 'error' && (
                <div className="rounded-xl px-4 py-3 bg-red-50 text-red-800 border border-red-200 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{submitStatus.msg}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-copper justify-center"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Submit Proof'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
